import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "../api/axios";
import { FiArrowLeft, FiUser, FiShield, FiDollarSign, FiTrendingUp, FiFileText, FiMapPin, FiCopy, FiCheck, FiUsers, FiBriefcase } from "react-icons/fi";

const UserViewPage = () => {
  const [searchParams] = useSearchParams();
  const viewUserId = searchParams.get("viewUserId");
  const [user, setUser] = useState(null);
  const [sharePurchases, setSharePurchases] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  const navigate = useNavigate();

  const fetchUserDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/admin/user/${viewUserId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setUser(data);
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSharePurchases = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/admin/share-purchases`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const userPurchases = data.filter(p => p.userId?._id === viewUserId || p.userId === viewUserId);
        setSharePurchases(userPurchases);
      }
    } catch (err) {
      console.error("Error fetching share purchases:", err);
    }
  };

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/transactions`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const userTransactions = data.filter(t => t.userId?._id === viewUserId || t.userId === viewUserId);
        setTransactions(userTransactions);
      }
    } catch (err) {
      console.error("Error fetching transactions:", err);
    }
  };

  useEffect(() => {
    if (viewUserId) {
      fetchUserDetails();
      fetchSharePurchases();
      fetchTransactions();
    }
  }, [viewUserId]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-slate-600">Loading user details...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-slate-600">User not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
            >
              <FiArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold text-slate-800">User Details</h1>
          </div>
          <div className="text-sm text-slate-500">
            Viewing as Admin
          </div>
        </div>

        {/* User Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {/* Basic Info */}
          <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-blue-600">
            <div className="flex items-center gap-2 mb-4">
              <FiUser className="text-blue-600" size={20} />
              <h3 className="font-bold text-slate-800">Basic Information</h3>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-slate-500">Name</p>
                <p className="font-semibold text-slate-800">{user.name}</p>
              </div>
              <div>
                <p className="text-slate-500">Unique ID</p>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-slate-800 font-mono">{user.uniqueId}</p>
                  <button onClick={() => handleCopy(user.uniqueId)} className="text-blue-600 hover:text-blue-800">
                    {copySuccess ? <FiCheck size={14} /> : <FiCopy size={14} />}
                  </button>
                </div>
              </div>
              <div>
                <p className="text-slate-500">Email</p>
                <p className="text-slate-800">{user.email}</p>
              </div>
              <div>
                <p className="text-slate-500">Mobile</p>
                <p className="text-slate-800">{user.mobile}</p>
              </div>
              <div>
                <p className="text-slate-500">Role</p>
                <span className={`px-2 py-1 rounded text-xs font-medium ${user.role === "admin" ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-800"}`}>
                  {user.role === "admin" ? "Admin" : "Member"}
                </span>
              </div>
              <div>
                <p className="text-slate-500">Status</p>
                <span className={`px-2 py-1 rounded text-xs font-medium ${user.status === "active" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                  {user.status}
                </span>
              </div>
              <div>
                <p className="text-slate-500">KYC Status</p>
                <span className={`px-2 py-1 rounded text-xs font-medium ${user.kycStatus === "verified" ? "bg-green-100 text-green-800" : user.kycStatus === "pending" ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"}`}>
                  {user.kycStatus?.toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          {/* Sponsor Info */}
          <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-cyan-600">
            <div className="flex items-center gap-2 mb-4">
              <FiUsers className="text-cyan-600" size={20} />
              <h3 className="font-bold text-slate-800">Sponsor Details</h3>
            </div>
            {user.sponsorDetails ? (
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-slate-500">Name</p>
                  <p className="font-semibold text-slate-800">{user.sponsorDetails.name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Unique ID</p>
                  <p className="font-mono text-slate-800">{user.sponsorDetails.uniqueId}</p>
                </div>
                <div>
                  <p className="text-slate-500">Email</p>
                  <p className="text-slate-800">{user.sponsorDetails.email}</p>
                </div>
                <div>
                  <p className="text-slate-500">Mobile</p>
                  <p className="text-slate-800">{user.sponsorDetails.mobile}</p>
                </div>
              </div>
            ) : (
              <p className="text-slate-500">No sponsor information</p>
            )}
          </div>

          {/* Partnership Status */}
          <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-purple-600">
            <div className="flex items-center gap-2 mb-4">
              <FiShield className="text-purple-600" size={20} />
              <h3 className="font-bold text-slate-800">Partnership Status</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">LBBP (Share)</span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${user.isShareActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                  {user.isShareActive ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">PU&AP (Product)</span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${user.isProductActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                  {user.isProductActive ? "Active" : "Inactive"}
                </span>
              </div>
              {user.lbbpId && (
                <div>
                  <p className="text-slate-500 text-sm">LBBP ID</p>
                  <p className="font-mono text-slate-800">{user.lbbpId}</p>
                </div>
              )}
              {user.puapId && (
                <div>
                  <p className="text-slate-500 text-sm">PU&AP ID</p>
                  <p className="font-mono text-slate-800">{user.puapId}</p>
                </div>
              )}
            </div>
          </div>

          {/* Wallet Summary */}
          <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-emerald-600">
            <div className="flex items-center gap-2 mb-4">
              <FiDollarSign className="text-emerald-600" size={20} />
              <h3 className="font-bold text-slate-800">Wallet Summary</h3>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">LBBP Wallet</span>
                <span className="font-bold text-slate-800">₹{Number(user.walletSummary?.lbbpWallet || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">PU&AP Wallet</span>
                <span className="font-bold text-slate-800">₹{Number(user.walletSummary?.puapWallet || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">E-Wallet</span>
                <span className="font-bold text-slate-800">₹{Number(user.walletSummary?.eWallet || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Total Withdrawn</span>
                <span className="font-bold text-slate-800">₹{Number(user.walletSummary?.totalWithdrawn || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">LBBP Pending</span>
                <span className="font-bold text-orange-600">₹{Number(user.walletSummary?.lbbpWithdrawalPending || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">PU&AP Pending</span>
                <span className="font-bold text-orange-600">₹{Number(user.walletSummary?.puapWithdrawalPending || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Income Summary */}
          <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-pink-600">
            <div className="flex items-center gap-2 mb-4">
              <FiTrendingUp className="text-pink-600" size={20} />
              <h3 className="font-bold text-slate-800">Income Summary</h3>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Level Income</span>
                <span className="font-bold text-slate-800">₹{Number(user.incomeSummary?.levelIncome || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">ROI Income</span>
                <span className="font-bold text-slate-800">₹{Number(user.incomeSummary?.roiIncome || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Royalty Income</span>
                <span className="font-bold text-slate-800">₹{Number(user.incomeSummary?.royaltyIncome || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Direct Bonus</span>
                <span className="font-bold text-slate-800">₹{Number(user.incomeSummary?.directBonus || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Matrix Income</span>
                <span className="font-bold text-slate-800">₹{Number(user.incomeSummary?.matrixIncome || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Generation Income</span>
                <span className="font-bold text-slate-800">₹{Number(user.incomeSummary?.generationIncome || 0).toLocaleString()}</span>
              </div>
              <div className="border-t pt-3 mt-3">
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-700">Total Income</span>
                  <span className="font-bold text-green-600">₹{Number(user.incomeSummary?.totalIncome || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Location Info */}
          <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-indigo-600">
            <div className="flex items-center gap-2 mb-4">
              <FiMapPin className="text-indigo-600" size={20} />
              <h3 className="font-bold text-slate-800">Location</h3>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-slate-500">State</p>
                <p className="text-slate-800">{user.state || "N/A"}</p>
              </div>
              <div>
                <p className="text-slate-500">District</p>
                <p className="text-slate-800">{user.district || "N/A"}</p>
              </div>
              <div>
                <p className="text-slate-500">Tahsil</p>
                <p className="text-slate-800">{user.tahsil || "N/A"}</p>
              </div>
              <div>
                <p className="text-slate-500">Pin Code</p>
                <p className="text-slate-800">{user.pinCode || "N/A"}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Details */}
        <div className="bg-white rounded-lg shadow-sm p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <FiFileText className="text-slate-600" size={20} />
            <h3 className="font-bold text-slate-800">Additional Information</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Referral Code</p>
              <p className="font-mono text-slate-800">{user.referralCode || "N/A"}</p>
            </div>
            <div>
              <p className="text-slate-500">User Type</p>
              <p className="text-slate-800 capitalize">{user.userType || "member"}</p>
            </div>
            <div>
              <p className="text-slate-500">Email Verified</p>
              <span className={`px-2 py-1 rounded text-xs font-medium ${user.emailVerified ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                {user.emailVerified ? "Yes" : "No"}
              </span>
            </div>
            <div>
              <p className="text-slate-500">Mobile Verified</p>
              <span className={`px-2 py-1 rounded text-xs font-medium ${user.mobileVerified ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                {user.mobileVerified ? "Yes" : "No"}
              </span>
            </div>
            <div>
              <p className="text-slate-500">Joined Date</p>
              <p className="text-slate-800">{user.createdAt ? new Date(user.createdAt).toLocaleDateString("en-IN") : "N/A"}</p>
            </div>
            <div>
              <p className="text-slate-500">Last Updated</p>
              <p className="text-slate-800">{user.updatedAt ? new Date(user.updatedAt).toLocaleDateString("en-IN") : "N/A"}</p>
            </div>
          </div>
        </div>

        {/* Share Purchases */}
        <div className="bg-white rounded-lg shadow-sm p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <FiBriefcase className="text-blue-600" size={20} />
            <h3 className="font-bold text-slate-800">Share Purchases (GCP Loan)</h3>
          </div>
          {sharePurchases.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Plan</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Amount</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">ROI Amount</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Weekly Payout</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Weeks Paid</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sharePurchases.map((purchase) => (
                    <tr key={purchase._id} className="border-b">
                      <td className="py-2 px-3">{purchase.planId?.name || "N/A"}</td>
                      <td className="py-2 px-3">₹{Number(purchase.amount || 0).toLocaleString()}</td>
                      <td className="py-2 px-3">₹{Number(purchase.roiAmount || 0).toLocaleString()}</td>
                      <td className="py-2 px-3">₹{Number(purchase.weeklyPayout || 0).toLocaleString()}</td>
                      <td className="py-2 px-3">{purchase.weeksPaid || 0} / {purchase.totalWeeks || 0}</td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${purchase.status === "active" ? "bg-green-100 text-green-800" : purchase.status === "completed" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-600"}`}>
                          {purchase.status?.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-500">No share purchases found</p>
          )}
        </div>

        {/* Transactions */}
        {/* <div className="bg-white rounded-lg shadow-sm p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <FiFileText className="text-slate-600" size={20} />
            <h3 className="font-bold text-slate-800">Recent Transactions</h3>
          </div>
          {transactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Type</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Amount</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Description</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.slice(0, 10).map((transaction) => (
                    <tr key={transaction._id} className="border-b">
                      <td className="py-2 px-3 capitalize">{transaction.type?.replace(/_/g, ' ') || "N/A"}</td>
                      <td className="py-2 px-3">₹{Number(transaction.amount || 0).toLocaleString()}</td>
                      <td className="py-2 px-3 text-slate-600">{transaction.description || "N/A"}</td>
                      <td className="py-2 px-3">{transaction.createdAt ? new Date(transaction.createdAt).toLocaleDateString("en-IN") : "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {transactions.length > 10 && (
                <p className="text-slate-500 text-sm mt-2">Showing 10 of {transactions.length} transactions</p>
              )}
            </div>
          ) : (
            <p className="text-slate-500">No transactions found</p>
          )}
        </div> */}

        {/* Team Info */}
        <div className="bg-white rounded-lg shadow-sm p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <FiUsers className="text-cyan-600" size={20} />
            <h3 className="font-bold text-slate-800">Team Information</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-slate-500">Direct Referrals</p>
              <p className="text-2xl font-bold text-slate-800">{user?.directReferrals?.length || 0}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-slate-500">Sponsor ID</p>
              <p className="text-lg font-semibold text-slate-800">{user?.sponsorId || "N/A"}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-slate-500">Referral Code</p>
              <p className="text-lg font-semibold text-slate-800">{user?.referralCode || "N/A"}</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="flex flex-wrap gap-3">
            {/* <button
              onClick={() => window.open(`/dashboard?viewUserId=${user._id}`, '_blank')}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <FiTrendingUp size={16} />
              View Dashboard
            </button> */}
            <button
              onClick={() => navigate(`/admin`)}
              className="bg-slate-600 text-white px-4 py-2 rounded-lg hover:bg-slate-700 flex items-center gap-2"
            >
              <FiArrowLeft size={16} />
              Back to Admin
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserViewPage;
